import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:shadow-red",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-border bg-transparent text-foreground shadow-sm hover:bg-muted hover:text-foreground hover:border-secondary",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: 
          "text-foreground hover:bg-muted hover:text-foreground",
        link: 
          "text-secondary underline-offset-4 hover:underline",
        // Premium luxury variants
        gold: 
          "bg-gradient-to-r from-secondary via-accent to-secondary-glow text-background font-semibold shadow-gold hover:shadow-glow-gold hover:-translate-y-0.5",
        red:
          "bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold shadow-red hover:shadow-lg hover:-translate-y-0.5",
        luxuryOutline:
          "border-2 border-secondary bg-transparent text-secondary font-semibold hover:bg-secondary/10 hover:shadow-gold",
        heroGold:
          "bg-gradient-to-r from-secondary via-accent to-secondary text-background font-semibold px-8 py-3 text-base shadow-gold hover:shadow-glow-gold hover:-translate-y-1 transition-all duration-300",
        heroOutline:
          "border-2 border-foreground/30 bg-foreground/5 text-foreground font-semibold px-8 py-3 text-base backdrop-blur-sm hover:bg-foreground/10 hover:border-secondary transition-all duration-300",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-4 text-xs",
        lg: "h-12 rounded-md px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
